import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addYears, addMonths, startOfMonth, isFuture } from 'date-fns';
import { Calendar } from './Calendar';

interface ElectionDetailsStepProps {
  onNext: (electionData: { type: string; date: Date; status: string }) => void;
  onBack: () => void;
  progressPercentage: number;
}

const electionTypes = [
  { id: 'general-even', name: 'General Election (Even Year)', description: 'Federal and most state elections occur in even-numbered years.' },
  { id: 'general-odd', name: 'General Election (Odd Year)', description: 'Many local and some state elections occur in odd-numbered years.' },
  { id: 'primary', name: 'Primary Election', description: 'Party nomination contests to determine general election candidates.' },
  { id: 'special', name: 'Special Election', description: 'Called to fill vacancies or address specific issues.' },
  { id: 'runoff', name: 'Runoff Election', description: 'Held when no candidate receives a majority in the initial election.' }
];

const incumbentStatuses = [
  { 
    id: 'incumbent',
    name: 'Incumbent',
    description: "You currently hold this position and are running for re-election."
  },
  {
    id: 'challenger',
    name: 'Challenger',
    description: "You are challenging an incumbent who currently holds this seat."
  },
  {
    id: 'open',
    name: 'Open Seat',
    description: "This seat has no incumbent running—often highly competitive."
  },
  {
    id: 'special',
    name: 'Special Election',
    description: "An unexpected vacancy; incumbency advantage is minimal or none."
  },
  {
    id: 'new',
    name: 'Newly Created Seat',
    description: "A new district or seat with no previous holder; high opportunity."
  }
];

export function ElectionDetailsStep({ onNext, onBack, progressPercentage }: ElectionDetailsStepProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (currentStep === 1 && selectedType) {
          handleNext();
        } else if (currentStep === 2 && selectedDate) {
          handleNext();
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [currentStep, selectedType, selectedDate]);

  const getNextElectionDate = (type: string): Date => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Helper function to get next election date for a specific month and day
    const getNextDate = (month: number, day: number, yearOffset: number = 0): Date => {
      const date = new Date(currentYear + yearOffset, month, day);
      return isFuture(date) ? date : new Date(currentYear + yearOffset + 1, month, day);
    };

    switch (type) {
      case 'general-even':
        // November 5th of next even year
        const nextEvenYear = currentYear + (currentYear % 2 === 0 ? 0 : 1);
        return getNextDate(10, 5, nextEvenYear - currentYear);

      case 'general-odd':
        // Default to November 5th, 2025 if it's in the future
        const nov2025 = new Date(2025, 10, 5);
        if (isFuture(nov2025)) return nov2025;
        
        // Otherwise get next odd year after 2025
        const nextOddYear = currentYear + (currentYear % 2 === 0 ? 1 : 2);
        return new Date(nextOddYear, 10, 5);

      case 'primary':
        // June 15th of the year before the next general election
        const primaryYear = currentYear + (now.getMonth() > 5 ? 1 : 0);
        return getNextDate(5, 15, primaryYear - currentYear);

      case 'special':
        // 90 days from now, rounded to first of month
        return addMonths(startOfMonth(now), 3);

      case 'runoff':
        // 30 days after next election date
        return addMonths(startOfMonth(now), 1);

      default:
        return now;
    }
  };

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    const nextDate = getNextElectionDate(type);
    setSelectedDate(nextDate);
    setCurrentStep(2);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setShowCalendar(false);
    setCurrentStep(3);
  };

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    if (selectedType && selectedDate) {
      onNext({
        type: selectedType,
        date: selectedDate,
        status: status
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedType) {
      setCurrentStep(2);
    } else if (currentStep === 2 && selectedDate) {
      setCurrentStep(3);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold mb-8">What type of election are you running in?</h2>
            <p className="text-gray-400 mb-8">
              Different election types have unique requirements and strategies.
            </p>
            <div className="grid gap-4">
              {electionTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeSelect(type.id)}
                  className="p-4 rounded-lg text-left transition-colors bg-white/5 hover:bg-white/10 border-2 border-transparent hover:border-primary"
                >
                  <div className="font-semibold">{type.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold mb-8">When is your election date?</h2>
            <p className="text-gray-400 mb-8">
              This helps us calculate campaign timelines and important deadlines.
            </p>
            <div className="relative">
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="w-full flex items-center px-4 py-3 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                <span>
                  {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a date'}
                </span>
              </button>
              
              {showCalendar && (
                <div className="absolute z-10 w-full mt-2">
                  <Calendar
                    selectedDate={selectedDate || new Date()}
                    onDateSelect={handleDateSelect}
                    minDate={new Date()}
                    maxDate={addYears(new Date(), 4)}
                  />
                </div>
              )}
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-3xl font-bold mb-8">What's your incumbent status?</h2>
            <p className="text-gray-400 mb-8">
              Your relationship to the current office holder affects campaign strategy.
            </p>
            <div className="grid gap-4">
              {incumbentStatuses.map((status) => (
                <button
                  key={status.id}
                  onClick={() => handleStatusSelect(status.id)}
                  className="p-4 rounded-lg text-left transition-colors bg-white/5 hover:bg-white/10 border-2 border-transparent hover:border-primary"
                >
                  <div className="font-semibold">{status.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{status.description}</div>
                </button>
              ))}
              <button
                onClick={() => handleStatusSelect('not-sure')}
                className="p-4 rounded-lg text-left transition-colors bg-white/5 hover:bg-white/10 border-2 border-transparent hover:border-primary"
              >
                <div className="font-semibold">Not Sure</div>
                <div className="text-sm text-gray-400 mt-1">I need help determining my incumbent status</div>
              </button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {renderStep()}

      <div className="flex justify-between pt-6">
        <button
          onClick={handleBack}
          className="flex items-center px-6 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        {currentStep < 3 && (
          <button
            onClick={handleNext}
            disabled={currentStep === 1 && !selectedType || currentStep === 2 && !selectedDate}
            className="flex items-center px-6 py-3 bg-[#026fb9] text-white rounded-lg hover:bg-[#026fb9]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        )}
      </div>
    </div>
  );
}